// ticket-reported-gaps.ts
//
// A2 (plan 20260915-1650, phase-01). Bước-0 finding: the parsed worker result
// was never persisted — `attempt_finished` carried a verdict only on failure
// routes — so the release flow had nothing to read when writing the draft
// MR's "Chưa kiểm chứng" section. Two pure helpers close that path:
//
//  - `extractReportedGapsFromResult`: pick the two OPTIONAL A2 arrays off an
//    AutopilotResultSchema-validated result (supervisor-loop.ts hands them to
//    journalAttemptOutcome, which journals them on the DONE attempt_finished;
//    mission-state-reducer.ts keeps the last declaration per ticket, redacted).
//    An EXPLICIT `[]` is kept as `[]` — dogfood 2026-09-15 (ME-2026-d74b4a):
//    the real implement worker declared "nothing left unverified" that way,
//    and the first cut collapsed it into "declared nothing". `[]` vs absent is
//    load-bearing (same rule as mission-declared-resources.ts).
//  - `collectDoneTicketReportedGaps`: from the projection, what the MR
//    description shows — DONE tickets only (a blocked ticket's self-report is
//    not part of the candidate), in ticket-id order: declared areas/work
//    prefixed with the ticket id, tickets that declared none, and tickets that
//    never declared.

import type { MissionState } from "./mission-state-reducer.js";

export interface ReportedGaps {
  uncovered_areas?: string[];
  remaining_work?: string[];
}

/** `undefined` when the field is absent or not an array; otherwise the non-empty strings (possibly `[]`). */
function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((v): v is string => typeof v === "string" && v.length > 0);
}

/** `undefined` only when the worker sent NEITHER field (pre-A2 worker). */
export function extractReportedGapsFromResult(result: unknown): ReportedGaps | undefined {
  if (typeof result !== "object" || result === null) return undefined;
  const record = result as Record<string, unknown>;
  const uncovered = stringArray(record.uncovered_areas);
  const remaining = stringArray(record.remaining_work);
  if (uncovered === undefined && remaining === undefined) return undefined;
  return {
    ...(uncovered !== undefined ? { uncovered_areas: uncovered } : {}),
    ...(remaining !== undefined ? { remaining_work: remaining } : {}),
  };
}

export interface DoneTicketReportedGapLines {
  /** `<ticket>: <area>` per declared unverified area. */
  uncoveredAreas: string[];
  /** `<ticket>: <work>` per declared item left for a person. */
  remainingWork: string[];
  /** Done tickets whose worker declared an empty `uncovered_areas` — a claim, not silence. */
  declaredNone: string[];
  /** Done tickets whose worker never sent the field. */
  notDeclared: string[];
}

export function collectDoneTicketReportedGaps(state: MissionState): DoneTicketReportedGapLines {
  const lines: DoneTicketReportedGapLines = {
    uncoveredAreas: [],
    remainingWork: [],
    declaredNone: [],
    notDeclared: [],
  };
  const doneTickets = Object.keys(state.tickets)
    .filter((ticket) => state.tickets[ticket] === "done")
    .sort();
  for (const ticket of doneTickets) {
    const gaps = state.ticket_reported_gaps[ticket];
    if (gaps === undefined) {
      lines.notDeclared.push(ticket);
      continue;
    }
    for (const area of gaps.uncovered_areas) lines.uncoveredAreas.push(`${ticket}: ${area}`);
    for (const work of gaps.remaining_work) lines.remainingWork.push(`${ticket}: ${work}`);
    if (gaps.uncovered_areas.length === 0) lines.declaredNone.push(ticket);
  }
  return lines;
}
